






import {
  defineComponent,
  ref,
  onMounted,
  onBeforeUnmount,
  PropType,
  computed,
} from '@vue/composition-api';
import { RouterV2Enum } from '@/router/router-names';
import { dcCreditSceneEnum } from '@/api/preApplyChoose/types';
import PreApplyChooseAPI from '@/api/preApplyChoose';
import { session } from '@/shared/utils/storage';
import { NewVersionEnum } from '@/enums/newVersionRoute';
import { jumpToNewVersion } from '@/shared/utils/jumpTo';
import { newCheckAndHandleLocationPermission } from '@/views_v2/pre-apply-choose/utils';
import { ubtSetupModuleExpose } from '@/shared/utils/ubt/ubt-setup';
import LoadingAnimate from '@/widgets/loading-animate/index.vue';
import { Toast } from '@hb/coffee';
import store from '@/store';
import { UserInfoStatus } from '@/enums/UserInfoStatus';
import { setNativeNavBar } from '@/api/native/utils';
import {
  AbExperimentKey,
  getAbResultByKey,
  isSkipCountdownGroup,
} from '@/shared/ab-experiment/shopMallExperiments';
import { createCreditPolling, PollingResultStatus } from './polling';

const USER_HAS_JUMPED_OUT_PAGE_KEY = 'userHasJumpedOutPage';

export default defineComponent({
  name: 'UnitWaitingPage',
  components: {
    LoadingAnimate,
  },
  props: {
    creditTaskId: {
      type: String,
      required: true,
    },
    isCheckDcContracts: {
      type: Boolean,
      default: false,
    },
    dcIdList: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    dcCreditScene: {
      type: String as PropType<dcCreditSceneEnum | string>,
      default: '',
    },
    applyMode: {
      type: String,
      default: '',
    },
    from: {
      type: String,
      default: '',
    },
    source: {
      type: String,
      default: '',
    },
  },
  setup(props: any, context: any) {
    const router = context.root.$router;

    const pollingTimer = ref<number | null>(null);
    const pollingProductId = ref('');
    const isFirstExpose = ref(true);
    /** AB 请求与轮询同时发起，跳转前 await 此 Promise 确保拿到正确结果 */
    let abResultPromise: Promise<boolean> = Promise.resolve(false);

    const dcGetCreditResult = async () => {
      // 失败或超时：判断是否勾选了贷超协议
      const hasDcContracts = props.isCheckDcContracts
            && props.dcIdList?.length > 0
            && props.dcCreditScene;

      if (hasDcContracts) {
        // 有贷超流程，先检查定位权限，然后调用贷超半流程授信
        const isOpenLocation = await newCheckAndHandleLocationPermission({
          needDoubleCheck: true,
          needCheckCityInfo: true,
          returnBoolean: true,
        });
        if (isOpenLocation) {
          try {
            // 定位权限检查完成（已开启或用户已确认），继续发起贷超授信
            const userProgress = store.state.userProfileProgress?.userInfoProgress;
            const fillInTheInformation = userProgress?.fillInTheInformation;
            const { taskId } = await PreApplyChooseAPI.dcCreditApply({
              commodityList: props.dcIdList.map((id: string) => Number(id)),
              scene: props.dcCreditScene as dcCreditSceneEnum,
              applyMode: props.applyMode,
              incompleteInfo: fillInTheInformation !== UserInfoStatus.Completed,
            });
            const source = 'unitWaitingPage';
            session.set(USER_HAS_JUMPED_OUT_PAGE_KEY, 1);
            jumpToNewVersion(NewVersionEnum.HALF_PROCESS_RESULT, true, `taskId=${taskId}&needCustomGoBack=true&source=${source}`);
          } catch (error) {
            jumpToNewVersion(NewVersionEnum.HALF_PROCESS_FAILED, true);
          }
        } else {
          // 定位权限检查失败，跳转到失败页
          jumpToNewVersion(NewVersionEnum.HALF_PROCESS_FAILED, true);
        }
        return;
      }

      // 没有贷超流程，跳转到失败页
      jumpToNewVersion(NewVersionEnum.HALF_PROCESS_FAILED, true);
    };

    // 创建轮询实例
    const { startPolling, stopPolling } = createCreditPolling(
      pollingTimer,
      props.creditTaskId,
      props.from,
      {
        onResult: async (result) => {
          pollingProductId.value = result.pollingProductId || '';
          if (isFirstExpose.value) {
            ubtSetupModuleExpose('finance_loan_shopMall_agreement_check_expose', {
              /** 投放来源 */
              from: props.from,
              /** 是否二次进入 */
              source: props.source,
              /** 机构ID列表 */
              pollingProductId: pollingProductId.value,
            });
            isFirstExpose.value = false;
          }
          // 成功：跳转前确保拿到 AB 结果（与轮询并行请求，此处 await 保证值正确）
          if (result.status === PollingResultStatus.SUCCESS) {
            const skipCountdown = await abResultPromise;
            router.replace({
              path: RouterV2Enum.fqMallPreApplySuccess,
              query: {
                creditTaskId: result.creditTaskId,
                from: props.from,
                source: props.source,
                skipCountdown: skipCountdown ? '1' : '0',
              },
            });
            return;
          }

          await dcGetCreditResult();
        },
        onError: (error) => {
          // 错误不中断轮询，继续下一次
        },
      },
    );

    onMounted(async () => {
      // 如果没有授信单号，调用贷超半流程授信
      if (!props.creditTaskId) {
        await dcGetCreditResult();
      } else {
        // AB 与轮询同时执行，跳转时 await abResultPromise 保证拿到正确 AB 值
        abResultPromise = getAbResultByKey(AbExperimentKey.FQ_MALL_SUCCESS_COUNTDOWN)
          .then((res) => isSkipCountdownGroup(res));
        startPolling();
      }
    });

    onBeforeUnmount(() => {
      stopPolling();
    });
  },
});
